import { createContext, useContext, useState, useCallback } from "react";

const FeedbackContext = createContext(null);

export const useFeedback = () => {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error("useFeedback must be used within a FeedbackProvider");
  }
  return context;
};

export const FeedbackProvider = ({ children }) => {
  const [shouldShowFeedback, setShouldShowFeedback] = useState(false);
  
  // Track significant actions to determine when to show feedback
  const getActionCount = () => {
    const count = parseInt(localStorage.getItem("syncchain_action_count") || "0", 10);
    return count;
  };
  
  const incrementActionCount = () => {
    const count = getActionCount() + 1;
    localStorage.setItem("syncchain_action_count", count.toString());
    return count;
  };
  
  const getLastFeedbackPrompt = () => {
    return localStorage.getItem("syncchain_last_feedback_prompt");
  };
  
  const setLastFeedbackPrompt = () => {
    localStorage.setItem("syncchain_last_feedback_prompt", new Date().toISOString());
  };
  
  const hasFeedbackBeenSubmitted = () => {
    return localStorage.getItem("syncchain_feedback_submitted") === "true";
  };
  
  const markFeedbackSubmitted = () => {
    localStorage.setItem("syncchain_feedback_submitted", "true");
  };
  
  // Check if enough time has passed since last prompt (24 hours)
  const canShowFeedback = () => {
    // Don't show if user already submitted feedback
    if (hasFeedbackBeenSubmitted()) return false;
    
    const lastPrompt = getLastFeedbackPrompt();
    if (!lastPrompt) return true;
    
    const hoursSinceLastPrompt = (Date.now() - new Date(lastPrompt).getTime()) / (1000 * 60 * 60);
    return hoursSinceLastPrompt >= 24; // Only show once per 24 hours
  };
  
  // Called after significant actions (search complete, export, etc.)
  const triggerFeedbackCheck = useCallback((actionType) => {
    const count = incrementActionCount();
    
    // Show feedback after 3rd significant action, then every 10 actions
    // But only if we can show it (24hr cooldown, not already submitted)
    const shouldTrigger = (count === 3 || (count > 3 && count % 10 === 0));
    
    if (shouldTrigger && canShowFeedback()) {
      // Small delay so it doesn't interrupt the action completion
      setTimeout(() => {
        setShouldShowFeedback(true);
        setLastFeedbackPrompt();
      }, 2000);
    }
  }, []);
  
  const showFeedback = useCallback(() => {
    setShouldShowFeedback(true);
  }, []);
  
  const hideFeedback = useCallback(() => {
    setShouldShowFeedback(false);
  }, []);
  
  const value = {
    shouldShowFeedback,
    showFeedback,
    hideFeedback,
    triggerFeedbackCheck,
    markFeedbackSubmitted,
  };
  
  return (
    <FeedbackContext.Provider value={value}>
      {children}
    </FeedbackContext.Provider>
  );
};
